// @dynamic-i18n-keys
// thumb.tooltip.*
import React from 'react';
import c from 'classnames';
import { useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-papi-types';
import { ThumbStatusTypes } from '@wix/da-ds/pkg/thumbs/types';
import DeviationFlag from '@wix/da-ds/pkg/Indicators/DeviationFlag';
import ThumbStatusIndicators from '@wix/da-ds/pkg/thumbs/ThumbStatusIndicators';
import SimpleTooltip from '@wix/da-ds/pkg/tooltips/dsPreset/SimpleTooltip';
import DeviationThumbDiscountLabel from '../DeviationThumbDiscountLabel';
import { useOverlayContext } from '../../../hooks/useOverlayContext';
import { getIsUserSubscribed, isHyped } from '../../../_helpers';
import { useIsCurrentUserDeviationAuthor } from '../../../../utils/hooks/useIsCurrentUserDeviationAuthor';
import { normalizePremiumFolderData } from '../../../../utils/normalizePremiumFolderData';
import { getIsAdopted, getIsOnSale, getIsPending } from '../../../../utils/adoptables';

import s from './DeviationThumbIndicators.scss';

interface Props {
  className?: string;
  deviation: PapiDeviation;

  /**
   * Will restrict the types of indicators that can be shown by this component. This is used when we want a placement to
   * support fewer indicator types. (e.g. only flags on hidden thumbs).
   *
   * Note: Restricting an indicator type will prevent it from being shown on this placement regardless of any related
   * OverlayContext values.
   *
   * Defaults to {@link AllowedIndicators.All}
   */
  allowedIndicators?: AllowedIndicators;
}

export enum AllowedIndicators {
  Flag = 'flag',
  Status = 'status',
  Discount = 'discount',
  All = 'all',
  None = 'none',
}

function getIndicators(deviation: PapiDeviation) {
  const indicators: ThumbStatusTypes[] = [];

  if (deviation.primaryTier) {
    indicators.push('in-tier');
  }
  const { isPaidFolder } = normalizePremiumFolderData(deviation.premiumFolderData);
  if (isPaidFolder) {
    indicators.push('premium-folder');
  }
  if (deviation.commission) {
    indicators.push('commission');
  }
  if (
    deviation.isAdoptable &&
    (getIsOnSale(deviation) || getIsPending(deviation) || getIsAdopted(deviation))
  ) {
    indicators.push('adoptable');
  }

  return indicators;
}

const DeviationThumbIndicators: React.FC<Props> = ({
  className,
  deviation,
  allowedIndicators = AllowedIndicators.All,
}) => {
  const { t } = useTranslation();
  const { showDailyDeviationLabel, showHypeLabel, showStashLabel, showThumbStatusIndicators } =
    useOverlayContext(deviation);
  const isOwner = useIsCurrentUserDeviationAuthor(deviation);

  const allowFlags =
    allowedIndicators === AllowedIndicators.Flag || allowedIndicators === AllowedIndicators.All;

  const allowStatusIndicators =
    allowedIndicators === AllowedIndicators.Status || allowedIndicators === AllowedIndicators.All;

  const allowDiscountIndicators =
    allowedIndicators === AllowedIndicators.Discount || allowedIndicators === AllowedIndicators.All;

  const showDailyDeviation = allowFlags && deviation.isDailyDeviation && showDailyDeviationLabel;
  const showHype = allowFlags && showHypeLabel && isHyped(deviation);
  const showStash = allowFlags && showStashLabel && !deviation.isPublished;

  const statusIndicators =
    showThumbStatusIndicators && allowStatusIndicators
      ? getIndicators(deviation)?.slice(0, showDailyDeviation ? 2 : 3)
      : [];

  return (
    <div className={c(s['root'], className)}>
      <div className={s['row-height']}></div>
      {showDailyDeviation && (
        <SimpleTooltip text={t('thumb.tooltip.dailyDeviation')} usePortal>
          <DeviationFlag variant="daily-deviation" size="large" className={s['dd']} />
        </SimpleTooltip>
      )}
      {showHype && <DeviationFlag variant="hype" size="large" className={s['hype']} />}
      {showStash && <DeviationFlag variant="stash" size="large" className={s['stash']} />}

      <ThumbStatusIndicators
        indicators={statusIndicators.map(type => ({
          type,
          tooltip: t(`thumb.tooltip.${type}`),
          hasAccess: getIsUserSubscribed(deviation) || isOwner,
        }))}
      />

      {allowDiscountIndicators && (
        <DeviationThumbDiscountLabel
          className={c(s['discount'], statusIndicators.length > 0 && s['with-standard-indicators'])}
          deviation={deviation}
        />
      )}
    </div>
  );
};
DeviationThumbIndicators.displayName = 'DeviationThumbIndicators';

export default DeviationThumbIndicators;
